/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-06-30
 */

import { useEffect, useState } from "react";
import { useQueryListContext } from "./QueryListContext";
import { Loading } from "./Loading";
import { Error } from "./Error";
import { ListControl } from "./ListControl";
import { ListSearch } from "./ListSearch";
import { ListColumns } from "./ListColumns";
import { ListRecords } from "./ListRecords";
import { Row, Col, Table, Card } from "react-bootstrap";
import { Edit } from "./Edit";
import { useAuth } from "../../../contexts/AuthContext";
import { CastType, DynamicObject, IFrontEndOnly, IQuery, IQueryColumnDetails, IQueryListComms, IWhere, QueryListProperties } from "../Interfaces";




export const QueryList: React.FC<QueryListProperties> = (props) => {

    const comms: IQueryListComms ={
        query: `${process.env.REACT_APP_BASE_URL}/${"query/data"}`,
        schema: `${process.env.REACT_APP_BASE_URL}/${"query/schema"}`
    }


    const {isAuthenticated, token, fetchData, fetchDataMulti}  = useAuth();

    const {ctxData, getData, setData} = useQueryListContext();

    // Column Details
    const [columnDetails, setColumnDetails] = useState<IQueryColumnDetails[]>([]);

    // Data Records
    const [dataRecords, setDataRecords] = useState<DynamicObject[]>([]);

    // Search Fields
    const [search, setSearch] = useState<Map<string, string>>(new Map<string, string>());

    // Page Support Hooks
    const [isLoading, setIsLoading] = useState(true);
    const [httpError, setHttpError] = useState(null);

    // Pagination Hooks:
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [isOpen, setIsOpen] = useState(false);

    const [activeStatusKey, setActiveStatusKey] = useState<string | null>(null);

    const toggleDropdown = () => {
          setIsOpen(!isOpen);
    };

    const frontEndOnly: IFrontEndOnly = {
        listTitle: props.listTitle,
        recordTitle: props.recordTitle,
        editTableName: props.editTableName,
        editHasCompoundKey: props.editHasCompoundKey
    }

    // Query State Hook
    const [queryState, setQueryState] = useState<IQuery>({
        tableName: props.tableName,
        select: props.select,
        groupBy: props.groupBy,
        join: props.join,
        orderBy: props.orderBy,
        where: props.where,
        mainTableOnly: false,
        includeExtraColumns: false,
        includeControlColumns: false,
        modifiedAfter: null,
        touchedBefore: null,
        isDirty: null,
        pageNum: 0,
        pageSize: 100,
        frontEndOnly: frontEndOnly
    }); 

    useEffect(() => {
        setData("comms", comms);
        setData("queryState", queryState);   
        setData("modal", props.modal);     
    }, []);

    useEffect(() => {
        const endpoints: string[] = [comms.schema, comms.query];
        
        setIsLoading(true);

        // console.log(`What is the endpoint shema; `, comms.schema);
        // console.log(`What is the endpoint query; `, comms.query);
        // console.log(queryState);

        fetchQueryData(endpoints, processData).catch((error: any) => {
            setIsLoading(false);
            setHttpError(error.message);
        }).finally(() => {
            setIsLoading(false);
        });

    }, [queryState]);


    const processData = (result: any) => {

        // console.log(`Resulting Data:`, result);

        // console.log(queryState);

        if(result && result.length === 2){

            const columnDetails: IQueryColumnDetails[] = result[0];
            const searchColumns: Map<string, string> = search;

            if(search.size === 0){
                for(const key in columnDetails) {
                    const columnDefinition:IQueryColumnDetails = columnDetails[key];
                    searchColumns.set(columnDefinition.fullColumnName as string, '');
                }
            }

            const data = result[1].data;
            const processedRecords: DynamicObject[] = [];
            const receivedUndefinedRecords: Array<Map<string, any>> = [];
            for(const key in data) {

                const recordData: DynamicObject = data[key]["data"];
                const recordControl: DynamicObject = data[key]["control"];
                const recordUndefined: Map<string, any> = data[key]["undefined"];

                // Merge recordData and recordControl together
                const concatRecord = {...recordData, ...recordControl};
                // console.log(JSON.stringify(concatRecord, null, 2));

                processedRecords.push(concatRecord);
                receivedUndefinedRecords.push(recordUndefined);
            }

            // Pagination 
            setPageNumber(result[1].pageNum);
            setPageSize(result[1].pageSize);
            setTotalRecords(result[1].numRecords);
            setTotalPages(Math.ceil(result[1].numRecords / result[1].pageSize));


            //console.log(columnDetails);
            setColumnDetails(columnDetails);
            setDataRecords(processedRecords);
            setSearch(searchColumns);          
        }
    }

    const fetchQueryData = async (endpoints: string[], processData: any) => {
        fetchDataMulti(endpoints, {method:"POST", body: JSON.stringify(queryState)}, processData);
    }

    

    if(isLoading) {
        return(
            <Loading />
        )
    }

    if(httpError) {
        return(
            <Error error={httpError} />
        )
    }

    const handlePageClick = (pageNum: number) => {  
        const query = {...queryState}
        query.pageNum = pageNum;
        setQueryState(query);
    }

    const handleSizeClick = (event: React.FocusEvent<HTMLInputElement>) => {
        const query = {...queryState}
        query.pageSize = Number(event.target.value) ?? 20;
        setQueryState(query);
    } 


      // Function to handle date parsing and formatting
    function parseDate(value: string): string {
        let date: Date | null = null;

        // Try to parse the value in various formats
        const datePatterns = [
            /^\d{2}-\d{2}-\d{4}$/, // dd-mm-yyyy
            /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2} [APM]{2}$/, // dd-mm-yyyy hh:mm:ss AM/PM
            /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/, // dd-mm-yyyy hh:mm:ss
            /^\d{4}-\d{2}-\d{2}$/, // yyyy-mm-dd (valid input)
        ];

        // Check if the value matches any of the patterns
        if (datePatterns[0].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [day, month, year] = value.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[1].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [datePart] = value.split(' '); 
            const [day, month, year] = datePart.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[2].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [datePart] = value.split(' ');
            const [day, month, year] = datePart.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[3].test(value)) {
            // Valid yyyy-mm-dd, no changes needed
            date = new Date(value);
        } else {
            // If no pattern matches, return the original value
            return value;
        }

        // If date is valid, format it as yyyy-mm-dd
        if (date && !isNaN(date.getTime())) {
            return date.toISOString().split('T')[0]; // Format as yyyy-mm-dd
        }

        // If invalid date, return the original value
        return value;
    }
    
    // const searchClicked = () => {
    //     let where: IWhere[] = [];

    //     search.forEach((value: string, column: string) => {
    //         if(value && value.length){
    //             const clause: IWhere = {
    //                 fullColumnName: column,
    //                 value: '%' + value + '%',
    //                 operator: "LIKE",
    //                 cast: CastType.STRING
    //             }
    //             where.push(clause);
    //         }
    //     });    

    //     const query = {...queryState}
    //     query.where = where;
    //     query.pageNum = 0;
    //     setQueryState(query);
    //     setActiveStatusKey(null);
    // }

    const searchClicked = () => {
      let where: IWhere[] = [];

      search.forEach((value: string, column: string) => {
          if (!value || !value.length) return;

          // Check for date fields
          if (column === "created" || column === "updated") {
              const formattedValue = parseDate(value);
              if (formattedValue) {
                  where.push({
                      fullColumnName: column,
                      value: formattedValue,
                      operator: "EQ",
                      cast: CastType.DATE
                  });
              }
          } else {
              // Default string LIKE filter
              where.push({
                  fullColumnName: column,
                  value: `%${value}%`,
                  operator: "LIKE",
                  cast: CastType.STRING
              });
          }
      });    

      const query = { ...queryState };
      query.where = where;
      query.pageNum = 0;

      setQueryState(query);
      setActiveStatusKey(null);
  };

    const clearClicked = () => {
        const query = {...queryState}
        query.where = props.where;
        setQueryState(query);
        setSearch(new Map<string, string>()); 
        setActiveStatusKey(null);
    }    


    const renderStatusLegend = () => {

            if (!props.hasIntegrationStatusLegend) return null;
          
            return (
              <div className="position-relative integration-status-legend">
                <button onClick={toggleDropdown} className="px-4 py-2 btn btn-dark d-flex align-items-center">
                  Integration Statuses
                  <svg xmlns="http://www.w3.org/2000/svg"  width="16" height="16" viewBox="0 0 24 24" fill="white" style={{ verticalAlign: 'middle' }}>
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm2 14h-4v-2h1v-4h-1v-2h3v6h1v2z" />
                  </svg>
                </button>
                { isOpen && (
                  <div className="integration-status-legend-content">
                    {props.integrationstatusLegend?.map((item, index) => (
                      <div className="item-rows" key={index} >
                        <div style={{ width: '1rem',  height: '1rem', backgroundColor: item.color, borderRadius: '0.125rem' }} />
                        <p className="status-name">
                          {item.status}:
                        </p>
                        <p className="status-description">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
        );
    }

    

    const handleFilterByStatusChange = (status: string | null) => {
      // Map of status configurations per table
        const tableStatusMap: Record<string, {fullColumnName: string; statuses: Record<string, string[]>}> = {
            sa_m24l_stock_adjustment: {
                fullColumnName: 'status',
                statuses: {
                  ERROR: ['ERROR'],
                  PROCESSING: ['TO_PROCESS'],
                  CLOSED: ['PROCESSED']
                }
              },
              sa_m24l_return_request: {
                fullColumnName: 'integration_status',
                statuses: {
                  ERROR: ['ERROR'],
                  PROCESSING: ['TO_SEND', 'SENT', 'ARRIVED', 'SUCCESS'],
                  CLOSED: ['CLOSED']
                }
              },
              sa_m24l_item: {
                fullColumnName: 'integration_status',
                statuses: {
                  ERROR: ['ERROR'],
                  PROCESSING: ['TO_SEND', 'SENT'],
                  CLOSED: ['SUCCESS']
                }
              },
              sa_m24l_stock_movement_in: {
                fullColumnName: 'integration_status',
                statuses: {
                  ERROR: ['ERROR'],
                  PROCESSING: ['TO_SEND', 'ARRIVED', 'SENT', 'SUCCESS', 'TO_CLOSE'],
                  CLOSED: ['CLOSED']
                }
              },
              sa_m24l_stock_movement_out: {
                fullColumnName: 'integration_status',
                statuses: {
                  ERROR: ['ERROR'],
                  PROCESSING: ['TO_SEND', 'PICKING', 'DISPATCHED', 'SENT', 'SUCCESS', 'TO_CLOSE'],
                  CLOSED: ['CLOSED']
                }
              },
              sa_m24l_pick_list: {
                fullColumnName: 'integration_status',
                statuses: {
                  ERROR: ['ERROR'],
                  PROCESSING: ['TO_SEND', 'PICKING', 'DISPATCHED', 'SENT', 'SUCCESS'],
                  CLOSED: ['CLOSED']
                }
              }
        };

        // Fallback in case tableName is missing or not matched
        const statusMap = tableStatusMap[props.tableName] ?? {
            ERROR: ['ERROR'],
            PROCESSING: [],
            CLOSED: []
        };

        const tableConfig = tableStatusMap[props.tableName];

        const statusValues = status && tableConfig ? tableConfig.statuses[status] ?? [] : [];
        const fullColumn = tableConfig?.fullColumnName ?? "integration_status"; // fallback just in case

        let where: IWhere[] = [];

        if (statusValues.length > 0) {
        where.push({
            fullColumnName: fullColumn,
            value: statusValues,
            operator: "IN",
            cast: CastType.STRING
        });
        }

        search.forEach((value, column) => {
        if (value && value.length && column !== fullColumn) {
            where.push({
            fullColumnName: column,
            value: '%' + value + '%',
            operator: "LIKE",
            cast: CastType.STRING
            });
        }
        });

        const newQueryState = { ...queryState, where, pageNum: 0 };
        setQueryState(newQueryState);
    };


    return (
        <>
                {(getData("primaryKeyValue") === undefined) 
                ?
                    <>
                        <Card>
                            <Card.Header className="pb-2">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                             <Card.Title>{props.listTitle}</Card.Title>
                             {renderStatusLegend()}
                            </div>
                                <ListControl 
                                  pageNumber={pageNumber} 
                                  pageSize={pageSize} 
                                  totalRecords={totalRecords} 
                                  totalPages={totalPages} 
                                  pageClick={handlePageClick} 
                                  sizeClick={handleSizeClick} 
                                  handleFilterByStatusChange={handleFilterByStatusChange} 
                                  activeStatusKey={activeStatusKey}
                                  onStatusChange={setActiveStatusKey}
                                  />
                            </Card.Header>

                            <Card.Body className="pt-0">
                                <div className="sa-table-container" >
                                <Table size="md" hover className="sa-table">
                                    <ListSearch 
                                     search={search} 
                                     setSearch={setSearch} 
                                     searchClicked={searchClicked} 
                                     clearClicked={clearClicked} 
                                     />
                                    <ListColumns columnDetails={columnDetails} />
                                    <ListRecords columnDetails={columnDetails} dataRecords={dataRecords} errorCategoryName={props.errorCategoryName} tableName={props.tableName} />
                                </Table>
                                </div>
                            </Card.Body>
                        </Card>                    
                    </>
                :                            
                    <>
                        <Card> 
                            <Card.Header>
                                <Card.Title>{props.listTitle}</Card.Title>
                            </Card.Header>

                            <Edit />
                        </Card>
                    </>
                }
            
        </>
    );
    
}

export function formatDate(dateString: string): string {
    var theDate = new Date(dateString);
    return theDate.toLocaleDateString().replaceAll("\/", "-") + " " + theDate.toLocaleTimeString();
}
